/**
 * CallRail Tracking Block
 *
 * Injection block that loads the CallRail dynamic number insertion script.
 * Site-wide: same account_hash and company_id across all properties.
 * CallRail identifies properties by the phone numbers already on the page.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useTrackingAllowed } from '../../shared/analytics';

const SCRIPT_ID = 'callrail-tracking-script';

function CallRailTrackingComponent(_props: BlockProps) {
  const accountHash = useBlockToken('account_hash', { defaultValue: '' });
  const companyId = useBlockToken('company_id', { defaultValue: '' });
  const trackingAllowed = useTrackingAllowed('analytics');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!trackingAllowed || !accountHash || !companyId) return;

    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://cdn.callrail.com/companies/${companyId}/${accountHash}/12/swap.js`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      mountedRef.current = false;
    };
  }, [trackingAllowed, accountHash, companyId]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'CallRailTracking',
  displayName: 'CallRail Tracking',
  tokens: {
    account_hash: {
      label: 'Account Hash',
      type: 'text',
      description: 'CallRail account hash (from the swap.js URL).',
      required: true,
    },
    company_id: {
      label: 'Company ID',
      type: 'text',
      description: 'CallRail company ID (numeric).',
      required: true,
    },
  },
};

export const callrailTrackingBlock: BlockDefinition = {
  id: 'callrail-tracking',
  name: 'CallRail Tracking',
  description: 'Dynamic number insertion for call tracking. Identifies properties by phone numbers on the page.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: CallRailTrackingComponent,
  contentSchema,
  tags: ['callrail', 'tracking', 'phone', 'call', 'analytics'],
};
